// CartPage.jsx (updated with proper LuxuryLoading usage)
import React, {useEffect, useState} from 'react';
import {motion, AnimatePresence} from 'framer-motion';
import Navbar from '../components/layout/Navbar';
import Footer from '../components/layout/Footer';
import CartItem from '../components/cart/CartItem';
import CartSummary from '../components/cart/CartSummary';
import EmptyCart from '../components/cart/EmptyCart';
import TrustBar from '../components/cart/TrustBar';
import LuxuryLoading from '../components/ui/LuxuryLoading';
import {Reveal} from '../components/animations/Reveal';
import {
    FaArrowLeft,
    FaShoppingBag,
    FaTrash,
    FaLeaf,
    FaBars,
} from 'react-icons/fa';
import {CartService, CartSessionManager} from "../api/cart.api";
import AvailableOffers from "../components/cart/AvailableOffers";
import OfferService from "../api/offer.api";

const CartPage = () => {
    const [cartItems, setCartItems] = useState([]);
    const [cartData, setCartData] = useState({
        items: [],
        subtotal: 0,
        discountTotal: 0,
        finalTotal: 0,
        appliedCartOffers: []
    });
    const [isLoading, setIsLoading] = useState(true);
    const [promoCode, setPromoCode] = useState('');
    const [isMobileSummaryOpen, setIsMobileSummaryOpen] = useState(false);
    const [isScrolled, setIsScrolled] = useState(false);
    const [checkoutLoading, setCheckoutLoading] = useState(false);
    const [promoLoading, setPromoLoading] = useState(false);
    const [itemActionLoading, setItemActionLoading] = useState({});

    useEffect(() => {
        const handleScroll = () => {
            setIsScrolled(window.scrollY > 50);
        };

        window.addEventListener('scroll', handleScroll);
        return () => window.removeEventListener('scroll', handleScroll);
    }, []);

    useEffect(() => {
        fetchCartData();
    }, []);

    useEffect(() => {
        console.log('Current cart state:', {
            cartItems,
            cartData,
            cartItemsLength: cartItems.length,
            cartDataItemsLength: cartData.items?.length || 0
        });
    }, [cartItems, cartData]);

    const fetchCartData = async () => {
        try {
            setIsLoading(true);
            const cart = await CartService.getCart();

            // console.log('Cart data from API:', cart);

            if (!cart || !cart.items || cart.items.length === 0) {
                console.log('Cart is empty or no items found');
                setCartItems([]);
                setCartData({
                    items: [],
                    subtotal: 0,
                    discountTotal: 0,
                    finalTotal: 0,
                    appliedCartOffers: []
                });
                return;
            }

            const transformedItems = cart.items.map(item => {
                console.log('Processing cart item:', item);

                return {
                    id: item._id || item.id,
                    productId: item.product?._id || item.product || item.productId,
                    name: item.name || item.product?.name || 'Product',
                    image: item.image || item.product?.images?.[0] || 'https://images.unsplash.com/photo-1556228453-efd6c1ff04f6?w=400&auto=format&fit=crop',
                    variant: item.variant?.name || item.variantId || 'Standard',
                    variantId: item.variant?.id || item.variantId,
                    price: item.price || item.variant?.price || item.product?.price || 0,
                    quantity: item.quantity || 1,
                    inStock: item.inStock !== false,
                    category: item.category || item.product?.category || 'Product',
                    originalPrice: item.originalPrice || item.price,
                    appliedOffers: item.appliedOffers || []
                };
            });

            console.log('Transformed items:', transformedItems);

            setCartData({
                items: cart.items || [],
                subtotal: cart.subtotal || 0,
                discountTotal: cart.discountTotal || 0,
                finalTotal: cart.finalTotal || 0,
                appliedCartOffers: cart.appliedCartOffers || []
            });

            setCartItems(transformedItems);
        } catch (error) {
            console.error('Error fetching cart:', error);
            setCartItems([]);
            setCartData({
                items: [],
                subtotal: 0,
                discountTotal: 0,
                finalTotal: 0,
                appliedCartOffers: []
            });
        } finally {
            setIsLoading(false);
        }
    };

    const shipping = cartData.subtotal > 100 ? 0 : 15;
    const total = cartData.finalTotal > 0 ? cartData.finalTotal : (cartData.subtotal + shipping - cartData.discountTotal);

    const updateQuantity = async (itemId, newQuantity) => {
        if (newQuantity < 1 || newQuantity > 10) return;

        try {
            setItemActionLoading(prev => ({ ...prev, [itemId]: true }));
            const result = await CartService.updateCartItem(itemId, newQuantity);

            if (result.cart) {
                const transformedItems = result.cart.items?.map(item => ({
                    id: item._id || item.id,
                    productId: item.product?._id || item.product,
                    name: item.name || item.product?.name || 'Product',
                    image: item.image || item.product?.images?.[0] || 'https://images.unsplash.com/photo-1556228453-efd6c1ff04f6?w=400&auto=format&fit=crop',
                    variant: item.variant?.name || item.variantId || 'Standard',
                    variantId: item.variant?.id || item.variantId,
                    price: item.price || item.variant?.price || item.product?.price || 0,
                    quantity: item.quantity || 1,
                    inStock: item.inStock !== false,
                    category: item.category || item.product?.category || 'Product',
                    originalPrice: item.originalPrice,
                    appliedOffers: item.appliedOffers || []
                })) || [];

                setCartData({
                    items: result.cart.items || [],
                    subtotal: result.cart.subtotal || 0,
                    discountTotal: result.cart.discountTotal || 0,
                    finalTotal: result.cart.finalTotal || 0,
                    appliedCartOffers: result.cart.appliedCartOffers || []
                });

                setCartItems(transformedItems);
            }
        } catch (error) {
            console.error('Error updating quantity:', error);
            alert(error.message || 'Failed to update quantity');
        } finally {
            setItemActionLoading(prev => ({ ...prev, [itemId]: false }));
        }
    };

    const removeItem = async (itemId) => {
        try {
            setItemActionLoading(prev => ({ ...prev, [itemId]: true }));
            const result = await CartService.removeFromCart(itemId);

            if (result.cart) {
                const transformedItems = result.cart.items?.map(item => ({
                    id: item._id || item.id,
                    productId: item.product?._id || item.product,
                    name: item.name || item.product?.name || 'Product',
                    image: item.image || item.product?.images?.[0] || 'https://images.unsplash.com/photo-1556228453-efd6c1ff04f6?w=400&auto=format&fit=crop',
                    variant: item.variant?.name || item.variantId || 'Standard',
                    variantId: item.variant?.id || item.variantId,
                    price: item.price || item.variant?.price || item.product?.price || 0,
                    quantity: item.quantity || 1,
                    inStock: item.inStock !== false,
                    category: item.category || item.product?.category || 'Product',
                    originalPrice: item.originalPrice,
                    appliedOffers: item.appliedOffers || []
                })) || [];

                setCartData({
                    items: result.cart.items || [],
                    subtotal: result.cart.subtotal || 0,
                    discountTotal: result.cart.discountTotal || 0,
                    finalTotal: result.cart.finalTotal || 0,
                    appliedCartOffers: result.cart.appliedCartOffers || []
                });

                setCartItems(transformedItems);
            }
        } catch (error) {
            console.error('Error removing item:', error);
            alert(error.message || 'Failed to remove item');
        } finally {
            setItemActionLoading(prev => ({ ...prev, [itemId]: false }));
        }
    };

    const moveToWishlist = (itemId) => {
        console.log('Move to wishlist:', itemId);
        removeItem(itemId);
    };

    const clearCart = async () => {
        if (!window.confirm('Are you sure you want to clear your cart?')) return;

        try {
            setIsLoading(true);
            const result = await CartService.clearCart();

            if (result.cart) {
                setCartItems([]);
                setCartData({
                    items: [],
                    subtotal: 0,
                    discountTotal: 0,
                    finalTotal: 0,
                    appliedCartOffers: []
                });
            }
        } catch (error) {
            console.error('Error clearing cart:', error);
            alert(error.message || 'Failed to clear cart');
        } finally {
            setIsLoading(false);
        }
    };

    const applyPromoCode = async () => {
        if (!promoCode.trim()) return;

        try {
            setPromoLoading(true);
            const sessionId = CartSessionManager.getSessionId();

            const currentCart = {
                subtotal: cartData.subtotal,
                items: cartData.items.map(item => ({
                    product: item.productId,
                    category: item.category,
                    price: item.price,
                    quantity: item.quantity,
                    variantId: item.variantId
                }))
            };

            const validation = await OfferService.validateOffer(
                promoCode,
                currentCart,
                sessionId
            );

            console.log('🔄 Offer validation:', validation);

            if (validation.isValid) {
                const applyResult = await OfferService.applyOffer(promoCode, sessionId);

                if (applyResult.success) {
                    await fetchCartData();
                    const discountAmount = applyResult.discount || 0;
                    alert(`✅ Promo code "${promoCode}" applied! Discount: $${discountAmount.toFixed(2)}`);
                    setPromoCode('');
                } else {
                    alert(`❌ Failed to apply promo code: ${applyResult.error || 'Unknown error'}`);
                }
            } else {
                alert(`❌ ${validation.message || 'Invalid promo code'}`);
            }
        } catch (error) {
            console.error('Error applying promo code:', error);
            alert(`Failed to apply promo code: ${error.message}`);
        } finally {
            setPromoLoading(false);
        }
    };

    const removeAppliedOffer = async () => {
        try {
            setPromoLoading(true);
            const sessionId = CartSessionManager.getSessionId();

            const result = await OfferService.removeOffer(sessionId);

            if (result.success) {
                await fetchCartData();
                alert('Offer removed successfully');
            } else {
                alert('Failed to remove offer: ' + (result.error || 'Unknown error'));
            }
        } catch (error) {
            console.error('Error removing offer:', error);
            alert('Failed to remove offer');
        } finally {
            setPromoLoading(false);
        }
    };

    const handleCheckout = async () => {
        try {
            setCheckoutLoading(true);
            const validation = await CartService.validateCartStock();

            if (!validation.isValid) {
                const outOfStockItems = validation.insufficientItems || [];
                if (outOfStockItems.length > 0) {
                    const itemNames = outOfStockItems.map(item => item.name).join(', ');
                    alert(`Some items in your cart are out of stock: ${itemNames}`);
                } else {
                    alert('Some items in your cart are out of stock or have insufficient quantity.');
                }
                return;
            }

            window.location.href = '/checkout';
        } catch (error) {
            console.error('Error during checkout:', error);
            alert('Failed to proceed to checkout: ' + error.message);
        } finally {
            setCheckoutLoading(false);
        }
    };

    if (isLoading && cartItems.length === 0) {
        return (
            <div className="min-h-screen bg-pearl flex items-center justify-center">
                <div className="text-center">
                    <LuxuryLoading size="large" />
                </div>
            </div>
        );
    }

    return (
        <div className="min-h-screen bg-pearl">
            <div
                className={`fixed top-0 left-0 right-0 z-50 transition-all duration-300 ${
                    isScrolled
                        ? 'bg-white/95 backdrop-blur-sm shadow-soft'
                        : 'bg-transparent'
                }`}
            >
                <Navbar/>
            </div>

            {/* Main Content */}
            <main className="relative min-h-screen lg:min-h-[90vh] overflow-hidden pt-10">
                <div className="container-luxury">
                    {/* Header */}
                    <div className="mb-8 md:mb-12">
                        <Reveal>
                            <div className="flex items-center justify-between gap-4 mb-4 pt-16 md:pt-16 lg:pt-16">
                                <button
                                    onClick={() => window.history.back()}
                                    className="flex items-center gap-2 text-sm text-charcoal/60 hover:text-champagne transition-colors"
                                >
                                    <FaArrowLeft className="w-4 h-4"/>
                                    <span className="hidden sm:inline">Continue Shopping</span>
                                    <span className="sm:hidden">Back</span>
                                </button>

                                {cartItems.length > 0 && (
                                    <button
                                        onClick={() => setIsMobileSummaryOpen(!isMobileSummaryOpen)}
                                        className="md:hidden flex items-center gap-2 px-3 py-2 bg-white rounded-xl border border-nude shadow-sm"
                                    >
                                        <FaBars className="w-4 h-4"/>
                                        <span className="text-sm font-medium">
                                            ${total.toFixed(2)}
                                        </span>
                                    </button>
                                )}
                            </div>
                        </Reveal>

                        <Reveal delay={0.1}>
                            <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-4">
                                <div>
                                    <h1 className="text-3xl sm:text-4xl md:text-5xl font-display font-light mb-1 sm:mb-2">
                                        Your Ritual
                                    </h1>
                                    <p className="text-sm sm:text-base text-charcoal/60">
                                        Review your selections and proceed to checkout
                                    </p>
                                </div>
                                <div className="flex items-center gap-2 sm:gap-3">
                                    <FaShoppingBag className="w-5 h-5 sm:w-6 sm:h-6 text-champagne"/>
                                    <span className="text-base sm:text-lg font-medium text-charcoal">
                                        {cartItems.length} {cartItems.length === 1 ? 'item' : 'items'}
                                    </span>
                                </div>
                            </div>
                        </Reveal>
                    </div>

                    {cartItems.length === 0 ? (
                        <EmptyCart/>
                    ) : (
                        <>
                            {/* Mobile Summary Overlay */}
                            <AnimatePresence>
                                {isMobileSummaryOpen && (
                                    <>
                                        <motion.div
                                            initial={{opacity: 0}}
                                            animate={{opacity: 1}}
                                            exit={{opacity: 0}}
                                            className="fixed inset-0 bg-black/20 backdrop-blur-sm z-40 md:hidden"
                                            onClick={() => setIsMobileSummaryOpen(false)}
                                        />
                                        <motion.div
                                            initial={{y: '100%'}}
                                            animate={{y: 0}}
                                            exit={{y: '100%'}}
                                            transition={{type: "spring", damping: 30}}
                                            className="fixed bottom-0 left-0 right-0 bg-white rounded-t-3xl shadow-2xl z-50 md:hidden p-6 max-h-[80vh] overflow-y-auto"
                                        >
                                            <div className="flex items-center justify-between mb-6 pb-4 border-b">
                                                <h2 className="text-xl font-medium">Order Summary</h2>
                                                <button
                                                    onClick={() => setIsMobileSummaryOpen(false)}
                                                    className="text-charcoal/40 hover:text-charcoal"
                                                >
                                                    ✕
                                                </button>
                                            </div>
                                            <CartSummary
                                                subtotal={cartData.subtotal}
                                                shipping={shipping}
                                                discountTotal={cartData.discountTotal}
                                                total={total}
                                                itemCount={cartItems.length}
                                                promoCode={promoCode}
                                                onPromoCodeChange={setPromoCode}
                                                onApplyPromoCode={applyPromoCode}
                                                onCheckout={handleCheckout}
                                                isLoading={checkoutLoading || promoLoading}
                                                appliedCartOffers={cartData.appliedCartOffers || []}
                                                compact={window.innerWidth < 768}
                                                sessionId={CartSessionManager.getSessionId()}
                                                onRemoveOffer={removeAppliedOffer}
                                            />

                                            <div className="mt-6">
                                                <AvailableOffers
                                                    onApplyOffer={(code) => {
                                                        setPromoCode(code);
                                                        setTimeout(() => applyPromoCode(), 100);
                                                    }}
                                                    cartSubtotal={cartData.subtotal}
                                                />
                                            </div>
                                        </motion.div>
                                    </>
                                )}
                            </AnimatePresence>

                            <div className="grid lg:grid-cols-3 gap-6 lg:gap-8 xl:gap-12">
                                {/* Cart Items */}
                                <div className="lg:col-span-2">
                                    <Reveal delay={0.2}>
                                        <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-3 mb-6">
                                            <h2 className="text-lg sm:text-xl font-medium text-charcoal">
                                                Cart Items
                                            </h2>
                                            <button
                                                onClick={clearCart}
                                                disabled={isLoading}
                                                className="flex items-center gap-2 text-sm text-charcoal/40 hover:text-champagne transition-colors w-fit disabled:opacity-50"
                                            >
                                                {isLoading ? (
                                                    <LuxuryLoading size="small" inline />
                                                ) : (
                                                    <>
                                                        <FaTrash className="w-3 h-3 sm:w-4 sm:h-4"/>
                                                        Clear All Items
                                                    </>
                                                )}
                                            </button>
                                        </div>
                                    </Reveal>

                                    <AnimatePresence>
                                        <div className="space-y-3 sm:space-y-4">
                                            {cartItems.map((item, index) => (
                                                <motion.div
                                                    key={item.id}
                                                    initial={{opacity: 0, y: 20}}
                                                    animate={{opacity: 1, y: 0}}
                                                    exit={{opacity: 0, y: -20, height: 0}}
                                                    transition={{delay: index * 0.1}}
                                                    layout
                                                >
                                                    <CartItem
                                                        item={item}
                                                        onUpdateQuantity={updateQuantity}
                                                        onRemove={removeItem}
                                                        onMoveToWishlist={moveToWishlist}
                                                        compact={window.innerWidth < 640}
                                                        isLoading={itemActionLoading[item.id]}
                                                    />
                                                </motion.div>
                                            ))}
                                        </div>
                                    </AnimatePresence>

                                    {/* Gift Note */}
                                    <Reveal delay={0.4}>
                                        <div className="mt-6 sm:mt-8 p-4 sm:p-6 bg-white rounded-2xl shadow-subtle">
                                            <div className="flex flex-col sm:flex-row sm:items-start gap-3 sm:gap-4">
                                                <div className="w-10 h-10 sm:w-12 sm:h-12 rounded-xl bg-sage/10 flex items-center justify-center flex-shrink-0">
                                                    <FaLeaf className="w-4 h-4 sm:w-5 sm:h-5 text-sage"/>
                                                </div>
                                                <div className="flex-1">
                                                    <h3 className="font-medium text-charcoal mb-2">
                                                        Add a Personal Touch
                                                    </h3>
                                                    <p className="text-xs sm:text-sm text-charcoal/60 mb-3 sm:mb-4">
                                                        Include a handwritten gift note with your order
                                                    </p>
                                                    <textarea
                                                        placeholder="Your message..."
                                                        className="w-full p-3 sm:p-4 bg-nude/20 rounded-xl border border-nude focus:border-champagne focus:outline-none resize-none text-sm"
                                                        rows={2}
                                                    />
                                                </div>
                                            </div>
                                        </div>
                                    </Reveal>
                                </div>

                                {/* Desktop Cart Summary */}
                                <div className="hidden lg:block lg:col-span-1">
                                    <CartSummary
                                        subtotal={cartData.subtotal}
                                        shipping={shipping}
                                        discountTotal={cartData.discountTotal}
                                        total={total}
                                        itemCount={cartItems.length}
                                        promoCode={promoCode}
                                        onPromoCodeChange={setPromoCode}
                                        onApplyPromoCode={applyPromoCode}
                                        onCheckout={handleCheckout}
                                        isLoading={checkoutLoading || promoLoading}
                                        appliedCartOffers={cartData.appliedCartOffers || []}
                                        sessionId={CartSessionManager.getSessionId()}
                                        onRemoveOffer={removeAppliedOffer}
                                    />
                                </div>
                            </div>
                        </>
                    )}

                    {/* Trust Bar */}
                    <TrustBar/>
                </div>
            </main>

            {/* Mobile Checkout Bar */}
            {cartItems.length > 0 && (
                <div className="lg:hidden fixed bottom-0 left-0 right-0 bg-white border-t border-nude/50 shadow-xl p-4 z-30">
                    <div className="container-luxury">
                        <div className="flex items-center justify-between gap-4">
                            <div>
                                <div className="text-sm text-charcoal/40">Total</div>
                                <div className="text-xl font-medium text-charcoal">
                                    ${total.toFixed(2)}
                                </div>
                            </div>
                            <button
                                onClick={handleCheckout}
                                disabled={checkoutLoading}
                                className="flex-1 max-w-[200px] px-6 py-3 bg-charcoal text-pearl rounded-full font-medium hover:bg-charcoal/90 transition-colors disabled:opacity-50 disabled:cursor-not-allowed flex items-center justify-center gap-2"
                            >
                                {checkoutLoading ? (
                                    <>
                                        <LuxuryLoading size="small" inline />
                                        Processing...
                                    </>
                                ) : (
                                    'Checkout'
                                )}
                            </button>
                        </div>
                    </div>
                </div>
            )}

            {/* Add padding for mobile checkout bar */}
            {cartItems.length > 0 && (
                <div className="lg:hidden h-20"/>
            )}

            <Footer/>
        </div>
    );
};

export default CartPage;